import { hashKey } from '@tanstack/react-query';
import type { Query, QueryClient } from '@tanstack/react-query';

import { clearSessionReturnPath } from '../api';

import { sessionKeys } from './query-keys';
import { sessionQueryOptions } from './queryOptions';
import { tellOtherTabs } from './sessionEvents';
import { LOGGED_OUT } from './types';
import type { PersonDetails, ServerSession, SessionUser, SignedInSession } from './types';

const isSessionQuery = (query: Query): boolean => query.queryKey[0] === sessionKeys.all[0];

/** The one entry that says who is signed in on this tab. */
export const isCurrentSessionQuery = (query: Query): boolean => query.queryHash === hashKey(sessionKeys.current());

/**
 * Drops every answer cached for the last person. The session entry itself stays,
 * because the screens watching it must hear the change: a removed entry tells them nothing.
 */
export const forgetCachedData = (queryClient: QueryClient): void => {
  queryClient.removeQueries({ predicate: (query) => !isSessionQuery(query) });
  queryClient.getMutationCache().clear();
};

/**
 * A read still in flight is called off first, so its late answer cannot undo this
 * one. The entry lives as long as the tab, watched or not, like a read of it does.
 */
export const replaceSession = (queryClient: QueryClient, session: ServerSession): void => {
  void queryClient.cancelQueries({ queryKey: sessionKeys.all });
  queryClient.setQueryDefaults(sessionKeys.all, { gcTime: Infinity });
  queryClient.setQueryData(sessionKeys.current(), session);
};

/** A sign-in in this tab: the session it opened, known here at once and in every other tab. */
export const openSessionHere = (queryClient: QueryClient, session: SignedInSession): void => {
  replaceSession(queryClient, session);
  tellOtherTabs({ type: 'signedIn', userId: session.user.id });
};

/** What a write just confirmed about the signed-in person. A field it leaves undefined is left as it was. */
export interface PersonPatch {
  readonly user?: Partial<Pick<SessionUser, 'name' | 'email'>>;
  readonly details?: Partial<PersonDetails>;
}

const withDefined = <T extends object>(base: T, patch: Partial<T> | undefined): T => {
  if (patch === undefined) return base;
  const next = { ...base };
  (Object.keys(patch) as (keyof T)[]).forEach((key) => {
    const value = patch[key];
    if (value !== undefined) next[key] = value;
  });
  return next;
};

// Details the tab has not read are never made up from a patch: the re-read describes them.
const patched = (session: ServerSession | undefined, patch: PersonPatch): ServerSession | undefined => {
  if (session?.status !== 'signedIn') return session;
  const { details } = session;
  return {
    ...session,
    user: withDefined<SessionUser>(session.user, patch.user),
    details: details.status === 'read' ? { status: 'read', value: withDefined<PersonDetails>(details.value, patch.details) } : details,
  };
};

/** The person read again from the server, after a write that changed what it keeps about them. */
export const readPersonAgain = (queryClient: QueryClient): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: sessionKeys.current() });

/**
 * Shows what a write just confirmed about the signed-in person at once, then reads the person again,
 * so the server's own answer replaces the patch and a patch never stands as if the server had said it.
 */
export const confirmPersonWrite = (queryClient: QueryClient, patch: PersonPatch): Promise<void> => {
  queryClient.setQueryData(sessionQueryOptions().queryKey, (session) => patched(session, patch));
  return readPersonAgain(queryClient);
};

/** A sign-out in this tab. */
export const endSessionHere = (queryClient: QueryClient): void => {
  forgetCachedData(queryClient);
  clearSessionReturnPath();
  replaceSession(queryClient, LOGGED_OUT);
  tellOtherTabs({ type: 'loggedOut' });
};

export const followLogoutElsewhere = (queryClient: QueryClient): void => {
  clearSessionReturnPath();
  replaceSession(queryClient, LOGGED_OUT);
};

export const announceSessionSwitch = (userId: string): void => {
  tellOtherTabs({ type: 'signedIn', userId });
};
